import React, { useState, useEffect } from 'react';
import { FileText, Plus, X, Save, Trash2, Download, Copy, Sparkles, BookOpen, CheckCircle, AlertTriangle, Target } from 'lucide-react';
import { emptyBrief, QUESTION_TEMPLATES, briefCompleteness, exportBriefMarkdown, type Brief } from './lib/engine';
import { aiPolishBrief } from './lib/ai-fallback';
import { SettingsButton } from './lib/SettingsButton';

const STORAGE = 'ca_briefbuilder_v1';

export default function App() {
  const [briefs, setBriefs] = useState<Brief[]>(() => JSON.parse(localStorage.getItem(STORAGE) || '[]'));
  const [active, setActive] = useState<Brief>(() => emptyBrief());
  const [aiOutput, setAiOutput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => { localStorage.setItem(STORAGE, JSON.stringify(briefs)); }, [briefs]);

  function update<K extends keyof Brief>(k: K, v: Brief[K]) { setActive({ ...active, [k]: v }); }
  function addToList(k: keyof Brief, val: string) { if (!val.trim()) return; const arr = active[k] as string[]; setActive({ ...active, [k]: [...arr, val.trim()] } as Brief); }
  function removeFromList(k: keyof Brief, ix: number) { const arr = (active[k] as string[]).filter((_, i) => i !== ix); setActive({ ...active, [k]: arr } as Brief); }
  function addStep() { setActive({ ...active, nextSteps: [...active.nextSteps, { what: '', owner: '', due: '' }] }); }
  function updateStep(ix: number, patch: Partial<Brief['nextSteps'][0]>) { setActive({ ...active, nextSteps: active.nextSteps.map((s, i) => i === ix ? { ...s, ...patch } : s) }); }
  function removeStep(ix: number) { setActive({ ...active, nextSteps: active.nextSteps.filter((_, i) => i !== ix) }); }

  function saveBrief() {
    const exists = briefs.find(b => b.id === active.id);
    if (exists) setBriefs(briefs.map(b => b.id === active.id ? active : b));
    else setBriefs([active, ...briefs]);
  }
  function loadBrief(id: string) { const b = briefs.find(x => x.id === id); if (b) setActive(b); }
  function deleteBrief(id: string) { setBriefs(briefs.filter(b => b.id !== id)); if (active.id === id) setActive(emptyBrief()); }
  function newBrief() { setActive(emptyBrief()); setAiOutput(''); }

  function downloadMd() {
    const blob = new Blob([exportBriefMarkdown(active)], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${active.projectName || 'brief'}.md`; a.click();
    URL.revokeObjectURL(url);
  }

  async function handleAi() {
    setAiLoading(true); setAiOutput('');
    let text = '';
    await aiPolishBrief(exportBriefMarkdown(active), (chunk) => { text += chunk; setAiOutput(text); });
    setAiLoading(false);
  }

  const completeness = briefCompleteness(active);
  const completenessColor = completeness < 50 ? 'text-red-400' : completeness < 80 ? 'text-amber-400' : 'text-emerald-400';

  return (
    <div className="min-h-screen bg-[#050b14] text-[#f0f0f0]">
      <header className="border-b border-white/10 bg-[#0a1628] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#d4af37] flex items-center justify-center text-[#0a1628] font-bold text-lg">C</div>
            <div>
              <h1 className="text-lg font-semibold tracking-tight">CatalystAgile <span className="text-[#d4af37]">BriefBuilder</span></h1>
              <p className="text-xs text-[#8a9bb8]">Discovery → project brief in one pass</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={newBrief} className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-white/10 flex items-center gap-1.5"><Plus className="w-3.5 h-3.5"/> New</button>
            <button onClick={downloadMd} className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-white/10 flex items-center gap-1.5"><Download className="w-3.5 h-3.5"/> Markdown</button>
            <button onClick={saveBrief} className="px-3 py-1.5 text-xs rounded-md bg-[#d4af37] text-[#0a1628] font-semibold hover:bg-[#e8c547] flex items-center gap-1.5"><Save className="w-3.5 h-3.5"/> Save</button>
            <SettingsButton className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-white/10 inline-flex items-center gap-1.5 transition-colors text-[#8a9bb8] hover:text-[#d4af37]" />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-3 space-y-4">
          <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4">
            <h2 className="text-sm font-semibold text-[#d4af37] mb-3 flex items-center gap-2"><Target className="w-4 h-4"/> Brief health</h2>
            <div className={`text-3xl font-bold ${completenessColor}`}>{completeness}%</div>
            <p className="text-xs text-[#8a9bb8] mt-1">Completeness across 10 sections</p>
          </div>
          <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4">
            <h2 className="text-sm font-semibold text-[#d4af37] mb-3 flex items-center gap-2"><BookOpen className="w-4 h-4"/> Question prompts</h2>
            <div className="space-y-1 max-h-[300px] overflow-y-auto">
              {QUESTION_TEMPLATES[active.callType].map((q, i) => (
                <div key={i} className="text-[11px] text-[#8a9bb8] p-2 rounded bg-[#132238]">{q}</div>
              ))}
            </div>
          </div>
          <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4">
            <h2 className="text-sm font-semibold text-[#d4af37] mb-3 flex items-center gap-2"><FileText className="w-4 h-4"/> Saved ({briefs.length})</h2>
            <div className="space-y-1 max-h-[260px] overflow-y-auto">
              {briefs.map(b => (
                <div key={b.id} className={`p-2 rounded text-xs cursor-pointer ${b.id === active.id ? 'bg-[#d4af37]/15 border border-[#d4af37]/30' : 'bg-[#132238] hover:bg-[#1a2d4a]'}`} onClick={() => loadBrief(b.id)}>
                  <div className="font-semibold truncate">{b.projectName || '(untitled)'}</div>
                  <div className="text-[10px] text-[#8a9bb8]">{b.clientName} · {b.date}</div>
                </div>
              ))}
            </div>
          </div>
        </aside>

        <section className="lg:col-span-9 space-y-4">
          <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-5">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="md:col-span-2"><label className="text-[10px] uppercase tracking-widest text-[#8a9bb8]">Project name</label><input value={active.projectName} onChange={e => update('projectName', e.target.value)} className="w-full mt-1 px-3 py-2 text-sm bg-[#132238] rounded-md border border-white/10 focus:border-[#d4af37] outline-none"/></div>
              <div><label className="text-[10px] uppercase tracking-widest text-[#8a9bb8]">Client</label><input value={active.clientName} onChange={e => update('clientName', e.target.value)} className="w-full mt-1 px-3 py-2 text-sm bg-[#132238] rounded-md border border-white/10 focus:border-[#d4af37] outline-none"/></div>
              <div><label className="text-[10px] uppercase tracking-widest text-[#8a9bb8]">Call type</label><select value={active.callType} onChange={e => update('callType', e.target.value as Brief['callType'])} className="w-full mt-1 px-3 py-2 text-sm bg-[#132238] rounded-md border border-white/10 focus:border-[#d4af37] outline-none">{['discovery','requirements','kickoff'].map(c => <option key={c} value={c}>{c}</option>)}</select></div>
            </div>
          </div>

          <TextSection label="Goal" value={active.goal} onChange={v => update('goal', v)} placeholder="What does success look like? (1-2 sentences)"/>
          <TextSection label="Problem" value={active.problem} onChange={v => update('problem', v)} placeholder="What's broken today? What's the cost of doing nothing?"/>
          <TextSection label="Audience" value={active.audience} onChange={v => update('audience', v)} placeholder="Who is this for? Be specific (role, seniority, context)."/>

          <ListSection label="Success metrics" items={active.successMetrics} onAdd={v => addToList('successMetrics', v)} onRemove={i => removeFromList('successMetrics', i)} placeholder="e.g. Reduce onboarding time from 5 days to 1"/>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ListSection label="In scope" items={active.scopeIn} onAdd={v => addToList('scopeIn', v)} onRemove={i => removeFromList('scopeIn', i)} placeholder="e.g. Customer record migration"/>
            <ListSection label="Out of scope" items={active.scopeOut} onAdd={v => addToList('scopeOut', v)} onRemove={i => removeFromList('scopeOut', i)} placeholder="e.g. Mobile app (Phase 2)"/>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ListSection label="Constraints" items={active.constraints} onAdd={v => addToList('constraints', v)} onRemove={i => removeFromList('constraints', i)} placeholder="e.g. Must launch before tax season"/>
            <ListSection label="Assumptions" items={active.assumptions} onAdd={v => addToList('assumptions', v)} onRemove={i => removeFromList('assumptions', i)} placeholder="e.g. Stripe webhook stays under 30s"/>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ListSection label="Risks" items={active.risks} onAdd={v => addToList('risks', v)} onRemove={i => removeFromList('risks', i)} placeholder="e.g. Single vendor lock-in"/>
            <ListSection label="Decisions made" items={active.decisions} onAdd={v => addToList('decisions', v)} onRemove={i => removeFromList('decisions', i)} placeholder="e.g. Use Cloudflare for hosting"/>
          </div>

          <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-5">
            <div className="flex items-center justify-between mb-3"><h3 className="text-sm font-semibold text-[#d4af37]">Next steps</h3><button onClick={addStep} className="text-xs px-2 py-1 rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-white/10 flex items-center gap-1"><Plus className="w-3 h-3"/> Add step</button></div>
            <div className="space-y-2">
              {active.nextSteps.map((s, ix) => (
                <div key={ix} className="grid grid-cols-12 gap-2 items-center">
                  <input placeholder="What" value={s.what} onChange={e => updateStep(ix, { what: e.target.value })} className="col-span-6 px-3 py-2 text-sm bg-[#132238] rounded-md border border-white/10 focus:border-[#d4af37] outline-none"/>
                  <input placeholder="Owner" value={s.owner} onChange={e => updateStep(ix, { owner: e.target.value })} className="col-span-3 px-3 py-2 text-sm bg-[#132238] rounded-md border border-white/10 focus:border-[#d4af37] outline-none"/>
                  <input type="date" value={s.due} onChange={e => updateStep(ix, { due: e.target.value })} className="col-span-2 px-3 py-2 text-sm bg-[#132238] rounded-md border border-white/10 focus:border-[#d4af37] outline-none"/>
                  <button onClick={() => removeStep(ix)} className="col-span-1 text-[#8a9bb8] hover:text-red-400"><Trash2 className="w-3.5 h-3.5"/></button>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[#0f1d32] rounded-xl border border-[#d4af37]/30 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-[#d4af37] flex items-center gap-2"><Sparkles className="w-4 h-4"/> AI polish (optional)</h3>
              <button onClick={handleAi} disabled={aiLoading} className="text-xs px-3 py-1.5 rounded-md bg-[#d4af37] text-[#0a1628] font-semibold hover:bg-[#e8c547] disabled:opacity-50 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5"/> {aiLoading ? 'Polishing...' : 'Polish brief'}</button>
            </div>
            {aiOutput && (
              <div className="mt-3 p-3 bg-[#132238] rounded-md text-xs whitespace-pre-wrap">{aiOutput}<button onClick={() => navigator.clipboard.writeText(aiOutput)} className="mt-3 text-[#d4af37] hover:text-[#e8c547] inline-flex items-center gap-1"><Copy className="w-3 h-3"/> Copy</button></div>
            )}
          </div>

          {briefs.find(b => b.id === active.id) && (
            <button onClick={() => deleteBrief(active.id)} className="text-xs text-[#8a9bb8] hover:text-red-400 inline-flex items-center gap-1"><Trash2 className="w-3 h-3"/> Delete this brief</button>
          )}
        </section>
      </main>
    </div>
  );
}

function TextSection({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-5">
      <h3 className="text-sm font-semibold text-[#d4af37] mb-2">{label}</h3>
      <textarea rows={2} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full px-3 py-2 text-sm bg-[#132238] rounded-md border border-white/10 focus:border-[#d4af37] outline-none resize-none"/>
    </div>
  );
}

function ListSection({ label, items, onAdd, onRemove, placeholder }: { label: string; items: string[]; onAdd: (v: string) => void; onRemove: (i: number) => void; placeholder?: string }) {
  const [draft, setDraft] = useState('');
  return (
    <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-5">
      <h3 className="text-sm font-semibold text-[#d4af37] mb-2">{label}</h3>
      <div className="flex gap-2 mb-2">
        <input value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { onAdd(draft); setDraft(''); } }} placeholder={placeholder} className="flex-1 px-3 py-2 text-sm bg-[#132238] rounded-md border border-white/10 focus:border-[#d4af37] outline-none"/>
        <button onClick={() => { onAdd(draft); setDraft(''); }} className="px-2 rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-white/10"><Plus className="w-3.5 h-3.5"/></button>
      </div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="flex items-center gap-2 text-xs text-[#f0f0f0] bg-[#132238] rounded px-2 py-1.5">
            <CheckCircle className="w-3.5 h-3.5 text-emerald-400 shrink-0"/>
            <span className="flex-1">{it}</span>
            <button onClick={() => onRemove(i)} className="text-[#8a9bb8] hover:text-red-400"><X className="w-3 h-3"/></button>
          </li>
        ))}
      </ul>
    </div>
  );
}
